/**
 * The arrange step's suggestion: the shortest order through the day, how to take it, and how to put it back.
 *
 * Three pure decisions, kept out of the screen because each has branches that a browser is a bad place to check
 * — an unmeasured day, two orders that measure the same, a stop the router never saw, a day whose stops include
 * an anchor and a midday return while the arrangement holds only the visits, and an undo that must stop being
 * offered the moment the user's own day moves on. The screen's job is to ask the router and render the answer;
 * which answer means what is here.
 *
 * **Untimed days only, and that is the rule rather than a limitation.** A day the user gave times to is driven
 * by those times: `assembleDay` sorts it into slot order when the plan is generated, so an order stored by this
 * suggestion could never change the drive — the times would still decide it — and the panel's own sentence
 * ("the order you touched will drive …") would be untrue of it. The suggestion is what the day's *statement*
 * decides, so a day that already stated its times has nothing left to suggest. `fixed` is that fact, and the
 * screen asks for the short measurement only when it is false.
 *
 * An untimed day is the opposite case, and the reason this suggestion is now the *only* way the order changes.
 * The engine used to reorder such a day itself — a plan came back in the optimiser's own order while the screen
 * showed the drag — and taking the suggestion was how the two were made to agree. It routes an arranged day in
 * its own order now, so what the user laid out is what gets driven, and this panel is an offer to change the
 * instruction itself rather than a correction of one the engine already substituted. That is still worth
 * offering, because the shortest drive is a real thing to want, because the two numbers here are the only
 * place the difference is ever priced, and because taking it is now the user's decision alone — which is why
 * it is one tap to take and one tap to put back (`SuggestionUndo` below).
 */

import type { PlanStopKind } from "@trippool/core";

/** One order of one day's points, as the router measured it. */
export interface MeasuredRoute {
  order: number[];
  distance_m: number | null;
  distances_known: boolean;
}

export interface OrderSuggestion {
  /** The permutation to apply, indexing the day's *stops* — anchors and the midday return included. */
  order: number[];
  /** The shortest road distance measured, in metres. */
  shortestM: number;
  /** What the order currently on the day measures. */
  asArrangedM: number;
}

/**
 * The suggestion, or null when there is nothing to suggest.
 *
 * Null is the common answer and each of its causes is a real state: the second measurement has not arrived,
 * the provider answered with no distances (so both sides are unknown rather than small), or the user's own
 * order is already the shortest — and a saving of zero metres is not a suggestion, it is noise on a screen
 * where every other line reports something.
 */
export function suggestOrder(input: {
  /** The optimised answer: the order the router drove when it was allowed to choose. */
  shortest: MeasuredRoute | undefined;
  /** The same points in the arrangement's own order, for the comparison. */
  asArranged: MeasuredRoute | undefined;
}): OrderSuggestion | null {
  const { shortest, asArranged } = input;
  if (shortest === undefined || asArranged === undefined) return null;
  if (!shortest.distances_known || !asArranged.distances_known) return null;
  if (shortest.distance_m === null || asArranged.distance_m === null) return null;
  if (asArranged.distance_m - shortest.distance_m <= 0) return null;

  return { order: shortest.order, shortestM: shortest.distance_m, asArrangedM: asArranged.distance_m };
}

/**
 * The arrangement reordered to match, or null when the permutation does not fit the day.
 *
 * The permutation indexes the day's **stops**, which are the visits *plus* the anchor the day starts from and
 * — on a rest day — the midday return. The arrangement holds the visits only, so the two are matched through
 * the stops' own kinds rather than by position: a permutation applied by position would shift a visit onto a
 * hotel's place in the list.
 *
 * A place with no coordinate is on the day and cannot be on the map, so no router has an opinion about where
 * it goes. The engine drops such a place from the route anyway (`prefilter` counts it as coordinateless), so
 * it is kept at the end rather than dropped from the arrangement — the one thing the user must never lose to
 * a suggestion is a place they chose.
 */
export function reorderedArrangement(input: {
  order: number[];
  stops: ReadonlyArray<{ id: string; kind: PlanStopKind }>;
  current: readonly string[];
}): string[] | null {
  const { order, stops, current } = input;
  if (order.length !== stops.length) return null;

  const drawn = new Set(stops.map((stop) => stop.id));
  const reordered = order
    .map((index) => stops[index]!)
    .filter((stop) => stop.kind === "visit")
    .map((stop) => stop.id);
  const undrawable = current.filter((id) => !drawn.has(id));
  /*
   * Nothing is invented and nothing is lost: the result holds exactly the places the day already had.
   *
   * The count is the whole check. Every visit the stops name came from this day's arrangement, and every id
   * the stops do not name is in `undrawable`, so equality here means the permutation partitioned the day
   * rather than replaced part of it — which is what a length mismatch from a stale answer would do.
   */
  if (reordered.length + undrawable.length !== current.length) return null;

  return [...reordered, ...undrawable];
}

/**
 * The order a taken suggestion can still be undone onto.
 *
 * Taking the suggestion is one tap (`reorderedArrangement` above), and the owner asked for the way back to be
 * one tap too: take it, see the day at the shorter distance, and put the arrangement back. That is why the
 * order the suggestion replaced is kept at all.
 *
 * `after` is what makes the keeping safe. The old order may only be restored while the day still holds
 * *exactly* what the suggestion made of it — the moment anything else touches the day, a drag, a place added
 * or taken off, a place deleted from the account, restoring that order would be the undo writing over an
 * edit the user made since, which is worse than having no undo at all. So the record is dropped the moment it
 * stops describing the day rather than being repaired into a best guess.
 */
export interface SuggestionUndo {
  /** The day the suggestion was taken on. */
  day: number;
  /** The order that day had before, which is what the undo restores. */
  before: string[];
  /** The order the suggestion produced — the only state of the day in which `before` may be restored. */
  after: string[];
}

/**
 * The undo still standing, or null when the day has moved on since the suggestion was taken.
 *
 * Judged against that day's own list and no other's: two days of a trip are two arrangements, so an edit on
 * one says nothing about whether the other's undo is still the user's own order.
 */
export function standingUndo(
  undo: SuggestionUndo,
  arrangement: Readonly<Record<number, string[] | undefined>>,
): SuggestionUndo | null {
  const current = arrangement[undo.day] ?? [];
  if (current.length !== undo.after.length) return null;
  return current.every((id, index) => id === undo.after[index]) ? undo : null;
}
